import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  enregistrerFicheCamion, enregistrerPhotosCamion,
} from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Truck, Plus, Trash2, Save, Image as ImageIcon, Check, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

// Televerse une photo et renvoie son identifiant. L'identifiant est le
// seul lien stable vers l'image : l'URL peut changer, pas lui. Le serveur
// controle le type reel, redimensionne et retire les metadonnees.
async function televerser(fichier) {
  const form = new FormData();
  form.append("fichier", fichier);
  const r = await axios.post(`${API}/fiches-camions/images`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  if (r.data?.file_id) return r.data.file_id;
  throw new Error("Identifiant du fichier introuvable");
}

// Memes valeurs que TYPES_PRISES cote serveur (fiches_camions.py).
const TYPES_PRISES = ["16 A", "32 A", "P17 mono 32 A", "P17 tri 16 A",
                      "P17 tri 32 A", "Autre"];
const ELEC_VIDE = { puissance_kw: "", alimentation: "", prises: [],
                    groupe_autonome: null, besoin_eau: null, remarques: "" };

function ouiNon(v) {
  return v === true ? "oui" : v === false ? "non" : "";
}

function ZonePhoto({ titre, aide, fileId, onChange, hauteur = "h-44" }) {
  const [envoi, setEnvoi] = useState(false);
  const choisir = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024)
      return toast.error("Photo trop lourde : 10 Mo maximum.");
    setEnvoi(true);
    try {
      onChange(await televerser(f));
      toast.success("Photo enregistrée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible");
    } finally {
      setEnvoi(false);
    }
  };
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">{titre}</p>
      <label
        className={`${hauteur} border border-dashed rounded-md flex items-center
          justify-center cursor-pointer overflow-hidden bg-muted/30 relative`}
      >
        {fileId ? (
          <img
            src={`${API}/files/${fileId}`}
            alt={titre}
            className="object-cover w-full h-full"
          />
        ) : (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <ImageIcon className="h-4 w-4" />
            {envoi ? "Envoi…" : "Choisir une photo"}
          </span>
        )}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={choisir} />
      </label>
      {aide && <p className="text-xs text-muted-foreground">{aide}</p>}
      {fileId && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="text-xs text-muted-foreground underline"
        >
          Retirer
        </button>
      )}
    </div>
  );
}

// integre : affichee comme onglet « Dossier » de Mon Food Truck. Les champs deja
// saisis dans Mon Food Truck (dimensions, vehicule, puissance, contact) n'y sont
// ni affiches ni renvoyes, pour qu'un onglet n'ecrase pas l'autre.
export default function FicheCamionPage({ integre = false }) {
  const { user } = useAuth();
  const ftId = user?.food_truck_id;
  const [f, setF] = useState(null);
  const [carte, setCarte] = useState([]);
  const [photoCamion, setPhotoCamion] = useState("");
  const [photosPlats, setPhotosPlats] = useState(["", "", "", ""]);
  const [enCours, setEnCours] = useState(false);
  const [propose, setPropose] = useState(null);
  const [elec, setElec] = useState(ELEC_VIDE);

  useEffect(() => { if (ftId) charger(); }, [ftId]);

  const charger = async () => {
    try {
      const r = await axios.get(`${API}/fiches-camions/ma-fiche`);
      const moi = r.data.fiche;
      if (!moi) return;
      setF(moi);
      setPhotoCamion(moi.photo_camion_id || "");
      const pp = moi.photos_plats_ids || [];
      setPhotosPlats([pp[0] || "", pp[1] || "", pp[2] || "",
                      pp[3] || ""]);
      setCarte(moi.carte_dossier || []);
      const d = moi.electricite_detail || {};
      setElec({
        puissance_kw: d.puissance_kw ?? "",
        alimentation: d.alimentation || "",
        prises: d.prises || [],
        groupe_autonome: d.groupe_autonome ?? null,
        besoin_eau: d.besoin_eau ?? null,
        remarques: d.remarques || "",
      });
    } catch {
      toast.error("Fiche indisponible");
    }
  };

  const champ = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const importer = async () => {
    try {
      const r = await axios.get(`${API}/fiches-camions/${ftId}/carte-proposee`);
      const l = r.data.lignes || [];
      if (!l.length) return toast.info("Rien à importer pour le moment");
      setPropose(l);
    } catch {
      toast.error("Import impossible");
    }
  };

  const retenir = (ligne) => {
    if (carte.length >= 8) return toast.error("Huit lignes au maximum");
    if (carte.some((c) => c.nom === ligne.nom)) return;
    setCarte([...carte, { nom: ligne.nom, prix: ligne.prix }]);
  };

  const elecChamp = (k, v) => setElec((e) => ({ ...e, [k]: v }));
  const elecRenseignee = () =>
    elec.puissance_kw !== "" || elec.alimentation || elec.prises.length > 0
    || elec.groupe_autonome !== null || elec.besoin_eau !== null
    || elec.remarques.trim();

  const enregistrer = async () => {
    const kw = elec.puissance_kw === "" ? null : parseFloat(
      String(elec.puissance_kw).replace(",", "."));
    if (kw !== null && (isNaN(kw) || kw < 0 || kw > 200))
      return toast.error("Puissance électrique : un nombre entre 0 et 200 kW.");
    setEnCours(true);
    try {
      await enregistrerFicheCamion(ftId, {
        ...(elecRenseignee() ? {
          electricite_detail: {
            puissance_kw: kw,
            alimentation: elec.alimentation,
            prises: elec.prises
              .filter((p) => p.type)
              .map((p) => ({ type: p.type,
                             nombre: parseInt(p.nombre, 10) || 1 })),
            groupe_autonome: elec.groupe_autonome,
            besoin_eau: elec.besoin_eau,
            remarques: elec.remarques,
          },
        } : {}),
        ...(integre ? {} : {
          longueur: parseFloat(f.longueur) || null,
          largeur: parseFloat(f.largeur) || null,
          hauteur: parseFloat(f.hauteur) || null,
          puissance_electrique: f.electricite || "",
          type_vehicule: f.type_vehicule || "",
          nom_contact: f.responsable || "",
          telephone: f.telephone || "",
        }),
        auvent_cote: f.auvent_cote || "",
        accroche_dossier: f.accroche_dossier || "",
        carte_dossier: carte,
      });
      await enregistrerPhotosCamion(ftId, {
        photo_camion_id: photoCamion,
        photos_plats_ids: photosPlats.filter(Boolean),
      });
      toast.success("Fiche enregistrée");
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setEnCours(false);
    }
  };

  if (!ftId)
    return (
      <div className="p-6 text-muted-foreground">
        Ce compte n'est rattaché à aucun food truck.
      </div>
    );
  if (!f) return <div className="p-6 text-muted-foreground">Chargement…</div>;

  const manquants = f.manquant || [];

  return (
    <div className={integre ? "space-y-5" : "p-4 md:p-6 space-y-5 max-w-4xl"}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          {!integre && (
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="h-6 w-6" /> Ma fiche camion
          </h1>
          )}
          <p className="text-sm text-muted-foreground">
            Ces informations alimentent les dossiers envoyés aux clients.
            Plus elles sont complètes, mieux votre camion est présenté.
          </p>
        </div>
        <Button onClick={enregistrer} disabled={enCours}>
          <Save className="h-4 w-4 mr-1" />
          {enCours ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>

      {manquants.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4 text-sm text-amber-900">
            Il manque encore : {manquants.join(", ")}.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Photos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ZonePhoto
            titre="Le camion"
            aide="Auvent ouvert, en service. Une photo avec des clients devant vaut mieux qu'un camion seul."
            fileId={photoCamion}
            onChange={setPhotoCamion}
            hauteur="h-52"
          />
          <div>
            <p className="text-sm font-medium mb-2">
              Vos plats — jusqu'à quatre
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[0, 1, 2, 3].map((i) => (
                <ZonePhoto
                  key={i}
                  titre={`Plat ${i + 1}`}
                  aide={i === 0 ? "Votre plat signature." : ""}
                  fileId={photosPlats[i]}
                  onChange={(v) =>
                    setPhotosPlats((p) => p.map((x, j) => (j === i ? v : x)))
                  }
                  hauteur="h-32"
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Toutes paraissent dans le dossier client, deux par rangée.
              Des plats différents valent mieux que quatre variantes du
              même.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Présentation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <textarea
            className="w-full border rounded-md p-2 text-sm"
            rows={4}
            maxLength={450}
            placeholder="Deux ou trois phrases sur votre cuisine, telles qu'elles apparaîtront dans le dossier client."
            value={f.accroche_dossier || ""}
            onChange={(e) => champ("accroche_dossier", e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {(f.accroche_dossier || "").length} / 450 caractères
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">La carte</CardTitle>
          <Button variant="outline" size="sm" onClick={importer}>
            Importer mes plats
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {propose && (
            <div className="border rounded-md p-2 bg-muted/30 space-y-1 max-h-60 overflow-auto">
              <p className="text-xs text-muted-foreground mb-1">
                Cliquez pour retenir une ligne. Les prix viennent de vos plats
                et de vos dossiers HEC : vérifiez-les, ils peuvent avoir changé.
              </p>
              {propose.map((l, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => retenir(l)}
                  className="w-full flex justify-between text-left text-sm hover:bg-muted rounded px-2 py-1"
                >
                  <span>{l.nom}</span>
                  <span className="text-muted-foreground">
                    {l.prix} · {l.source}
                  </span>
                </button>
              ))}
              <button
                type="button"
                className="text-xs underline text-muted-foreground mt-1"
                onClick={() => setPropose(null)}
              >
                Fermer
              </button>
            </div>
          )}

          {carte.map((l, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input
                className="flex-1"
                placeholder="Nom du plat"
                value={l.nom}
                onChange={(e) =>
                  setCarte(carte.map((c, j) =>
                    j === i ? { ...c, nom: e.target.value } : c))
                }
              />
              <Input
                className="w-28"
                placeholder="Prix"
                value={l.prix}
                onChange={(e) =>
                  setCarte(carte.map((c, j) =>
                    j === i ? { ...c, prix: e.target.value } : c))
                }
              />
              <Button variant="ghost" size="icon"
                      onClick={() => setCarte(carte.filter((_, j) => j !== i))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {carte.length < 8 && (
            <Button variant="outline" size="sm"
                    onClick={() => setCarte([...carte, { nom: "", prix: "" }])}>
              <Plus className="h-4 w-4 mr-1" /> Ajouter une ligne
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            Huit lignes au maximum. Il s'agit de donner envie, pas de
            reproduire toute la carte.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{integre ? "Ouverture de l'auvent" : "Encombrement et besoins"}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          {[["longueur", "Longueur (m)"], ["largeur", "Largeur (m)"],
            ["hauteur", "Hauteur (m)"]].map(([k, lib]) => (
            <div key={k} className={integre ? "hidden" : "space-y-1"}>
              <label className="text-xs text-muted-foreground">{lib}</label>
              <Input type="number" step="0.05" min="0.5" max="25"
                     value={f[k] ?? ""}
                     onChange={(e) => champ(k, e.target.value)} />
            </div>
          ))}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Ouverture auvent
            </label>
            <select className="w-full border rounded-md h-9 px-2 text-sm"
                    value={f.auvent_cote || ""}
                    onChange={(e) => champ("auvent_cote", e.target.value)}>
              <option value="">À préciser</option>
              <option>Côté conducteur</option>
              <option>Côté passager</option>
              <option>Les deux côtés</option>
            </select>
          </div>
          <div className={integre ? "hidden" : "space-y-1"}>
            <label className="text-xs text-muted-foreground">Véhicule</label>
            <select className="w-full border rounded-md h-9 px-2 text-sm"
                    value={f.type_vehicule || ""}
                    onChange={(e) => champ("type_vehicule", e.target.value)}>
              <option value="">À préciser</option>
              <option>Camion</option>
              <option>Remorque</option>
              <option>Triporteur</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4" /> Besoins électriques
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p className="text-xs text-muted-foreground">
            Ces informations calculent automatiquement la puissance et les
            prises à demander à l'organisateur dans le dossier client.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label htmlFor="elec-kw" className="text-xs text-muted-foreground">
                Puissance totale (kW)
              </label>
              <Input id="elec-kw" inputMode="decimal" placeholder="ex. 7,2"
                     value={elec.puissance_kw}
                     onChange={(e) => elecChamp("puissance_kw", e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="elec-alim" className="text-xs text-muted-foreground">
                Alimentation
              </label>
              <select id="elec-alim" className="w-full border rounded-md h-9 px-2 text-sm"
                      value={elec.alimentation}
                      onChange={(e) => elecChamp("alimentation", e.target.value)}>
                <option value="">À préciser</option>
                <option value="mono">Monophasé</option>
                <option value="tri">Triphasé</option>
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="elec-groupe" className="text-xs text-muted-foreground">
                Groupe électrogène autonome
              </label>
              <select id="elec-groupe" className="w-full border rounded-md h-9 px-2 text-sm"
                      value={ouiNon(elec.groupe_autonome)}
                      onChange={(e) => elecChamp("groupe_autonome",
                        e.target.value === "" ? null : e.target.value === "oui")}>
                <option value="">À préciser</option>
                <option value="oui">Oui, le camion est autonome</option>
                <option value="non">Non, branchement sur le site</option>
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="elec-eau" className="text-xs text-muted-foreground">
                Besoin d'un point d'eau
              </label>
              <select id="elec-eau" className="w-full border rounded-md h-9 px-2 text-sm"
                      value={ouiNon(elec.besoin_eau)}
                      onChange={(e) => elecChamp("besoin_eau",
                        e.target.value === "" ? null : e.target.value === "oui")}>
                <option value="">À préciser</option>
                <option value="oui">Oui</option>
                <option value="non">Non</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Prises nécessaires</p>
            {elec.prises.map((p, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select className="flex-1 border rounded-md h-9 px-2 text-sm"
                        aria-label={`Type de prise ${i + 1}`}
                        value={p.type}
                        onChange={(e) => elecChamp("prises", elec.prises.map(
                          (x, j) => (j === i ? { ...x, type: e.target.value } : x)))}>
                  <option value="">Type de prise</option>
                  {TYPES_PRISES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <Input className="w-24" type="number" min="1" max="10"
                       aria-label={`Nombre de prises ${i + 1}`}
                       value={p.nombre}
                       onChange={(e) => elecChamp("prises", elec.prises.map(
                         (x, j) => (j === i ? { ...x, nombre: e.target.value } : x)))} />
                <Button variant="ghost" size="icon"
                        aria-label="Retirer cette prise"
                        onClick={() => elecChamp("prises",
                          elec.prises.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {elec.prises.length < 6 && (
              <Button variant="outline" size="sm"
                      onClick={() => elecChamp("prises",
                        [...elec.prises, { type: "16 A", nombre: 1 }])}>
                <Plus className="h-4 w-4 mr-1" /> Ajouter une prise
              </Button>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="elec-rq" className="text-xs text-muted-foreground">
              Remarques (facultatif)
            </label>
            <Input id="elec-rq" maxLength={300}
                   placeholder="ex. rallonge de 25 m fournie"
                   value={elec.remarques}
                   onChange={(e) => elecChamp("remarques", e.target.value)} />
          </div>
          {f.electricite && (
            <p className="text-xs text-muted-foreground">
              Ancienne saisie : « {f.electricite} ». Elle reste utilisée tant
              que les champs ci-dessus ne sont pas remplis.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className={integre ? "hidden" : ""}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Contact sur place</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input placeholder="Nom du responsable" value={f.responsable || ""}
                 onChange={(e) => champ("responsable", e.target.value)} />
          <Input placeholder="Téléphone" value={f.telephone || ""}
                 onChange={(e) => champ("telephone", e.target.value)} />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={enregistrer} disabled={enCours}>
          <Check className="h-4 w-4 mr-1" />
          {enCours ? "Enregistrement…" : "Enregistrer ma fiche"}
        </Button>
      </div>
    </div>
  );
}
